import React, { useState } from 'react';
import { Plus, X, Trash2, Upload, Image } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Componente de upload de imagem para opções de personalização
const OptionImageUpload = ({ currentImage, onImageChange, label }) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione apenas arquivos de imagem');
      return;
    }

    // Validar tamanho (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo 5MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onImageChange(response.data.url);
      toast.success('Imagem enviada!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erro ao enviar imagem');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-xs text-muted-foreground">{label}</label>
      <div className="flex items-center gap-3">
        {currentImage ? (
          <div className="relative w-16 h-16 border border-border overflow-hidden">
            <img src={currentImage} alt="Preview" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onImageChange('')}
              className="absolute -top-1 -right-1 bg-destructive text-white rounded-full p-0.5"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="w-16 h-16 border border-dashed border-border flex items-center justify-center bg-muted/30">
            <Image className="w-6 h-6 text-muted-foreground" />
          </div>
        )}
        <label className={`flex items-center gap-2 px-3 py-2 border border-border text-xs cursor-pointer hover:bg-muted/30 transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
          <Upload className="w-4 h-4" />
          {uploading ? 'Enviando...' : 'Upload'}
          <input
            type="file"
            accept="image/*"
            onChange={handleUpload}
            disabled={uploading}
            className="hidden"
          />
        </label>
      </div>
    </div>
  );
};

const ProductCustomization = ({ productData, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('fabric');
  const freeEnabled = !!productData.free_dimensions_enabled;
  const freeMode = productData.free_dimensions_mode || 'm2';

  const addFabricOption = () => {
    const newOption = {
      id: Date.now().toString(),
      name: '',
      price_per_m2: 0,
      image: '',
    };
    onUpdate('fabric_options', [...(productData.fabric_options || []), newOption]);
  };

  const updateFabricOption = (index, field, value) => {
    const updated = [...(productData.fabric_options || [])];
    updated[index][field] = field === 'price_per_m2' ? parseFloat(value) || 0 : value;
    onUpdate('fabric_options', updated);
  };

  const removeFabricOption = (index) => {
    const updated = (productData.fabric_options || []).filter((_, i) => i !== index);
    onUpdate('fabric_options', updated);
  };

  const addSizeOption = () => {
    const newOption = {
      id: Date.now().toString(),
      name: '',
      m2_needed: 0,
      description: '',
    };
    onUpdate('size_options', [...(productData.size_options || []), newOption]);
  };

  const updateSizeOption = (index, field, value) => {
    const updated = [...(productData.size_options || [])];
    updated[index][field] = field === 'm2_needed' ? parseFloat(value) || 0 : value;
    onUpdate('size_options', updated);
  };

  const removeSizeOption = (index) => {
    const updated = (productData.size_options || []).filter((_, i) => i !== index);
    onUpdate('size_options', updated);
  };

  const addMaterialOption = () => {
    const newOption = {
      id: Date.now().toString(),
      name: '',
      price_extra: 0,
      description: '',
      image: '',
    };
    onUpdate('material_options', [...(productData.material_options || []), newOption]);
  };

  const updateMaterialOption = (index, field, value) => {
    const updated = [...(productData.material_options || [])];
    updated[index][field] = field === 'price_extra' ? parseFloat(value) || 0 : value;
    onUpdate('material_options', updated);
  };

  const removeMaterialOption = (index) => {
    const updated = (productData.material_options || []).filter((_, i) => i !== index);
    onUpdate('material_options', updated);
  };

  return (
    <div className="border border-border p-6 mt-6">
      <div className="flex items-center gap-3 mb-6">
        <input
          type="checkbox"
          id="is_customizable"
          data-testid="is-customizable-checkbox"
          checked={productData.is_customizable || false}
          onChange={(e) => onUpdate('is_customizable', e.target.checked)}
          className="w-4 h-4"
        />
        <label htmlFor="is_customizable" className="font-accent text-xs uppercase tracking-widest">
          Produto Personalizável (Cliente escolhe tecido/tamanho/material)
        </label>
      </div>

      {productData.is_customizable && (
        <>
          {/* Tabs */}
          <div className="flex gap-4 border-b border-border mb-6">
            <button
              type="button"
              onClick={() => setActiveTab('fabric')}
              className={`pb-2 text-sm uppercase tracking-widest transition-colors ${
                activeTab === 'fabric' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'
              }`}
            >
              Tecidos
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('size')}
              className={`pb-2 text-sm uppercase tracking-widest transition-colors ${
                activeTab === 'size' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'
              }`}
            >
              Tamanhos
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('material')}
              className={`pb-2 text-sm uppercase tracking-widest transition-colors ${
                activeTab === 'material' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'
              }`}
            >
              Materiais
            </button>
            <button
              type="button"
              data-testid="tab-free-dimensions"
              onClick={() => setActiveTab('free')}
              className={`pb-2 text-sm uppercase tracking-widest transition-colors ${
                activeTab === 'free' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'
              }`}
            >
              Medidas Livres
            </button>
          </div>

          {activeTab === 'free' && (
            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 p-4 rounded text-sm">
                <p className="font-medium text-purple-800 mb-2">Configurador Livre — cliente digita as medidas</p>
                <p className="text-purple-700 text-xs">
                  Quando ativado, além (ou no lugar) dos tamanhos pré-definidos acima, o cliente poderá
                  informar suas próprias dimensões e o preço será recalculado automaticamente.
                </p>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="free-dimensions-enabled"
                  checked={freeEnabled}
                  onChange={(e) => onUpdate('free_dimensions_enabled', e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm">Habilitar medidas livres neste produto</span>
              </label>

              {freeEnabled && (
                <div className="space-y-4 pl-7 border-l-2 border-purple-200">
                  <div>
                    <label className="text-xs text-muted-foreground mb-2 block">Modo de cálculo</label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        data-testid="free-mode-m2"
                        onClick={() => onUpdate('free_dimensions_mode', 'm2')}
                        className={`px-4 py-2 text-xs uppercase tracking-widest border transition-all ${
                          freeMode === 'm2' ? 'bg-primary text-white border-primary' : 'border-border hover:border-primary'
                        }`}
                      >
                        Por m² (largura × profundidade)
                      </button>
                      <button
                        type="button"
                        data-testid="free-mode-linear"
                        onClick={() => onUpdate('free_dimensions_mode', 'linear')}
                        className={`px-4 py-2 text-xs uppercase tracking-widest border transition-all ${
                          freeMode === 'linear' ? 'bg-primary text-white border-primary' : 'border-border hover:border-primary'
                        }`}
                      >
                        Por metro linear (comprimento)
                      </button>
                    </div>
                  </div>

                  {freeMode === 'm2' ? (
                    <div className="text-xs text-muted-foreground bg-neutral-50 p-3 border border-border">
                      Preço = Base + (largura(m) × profundidade(m)) × preço/m² do tecido escolhido + Material extra.
                      É necessário ao menos 1 tecido cadastrado.
                    </div>
                  ) : (
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">
                        Preço por metro linear (R$)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Ex: 350.00"
                        data-testid="price-per-linear-meter"
                        value={productData.price_per_linear_meter ?? 0}
                        onChange={(e) => onUpdate('price_per_linear_meter', parseFloat(e.target.value) || 0)}
                        className="w-full max-w-xs bg-transparent border-b border-border focus:border-primary px-0 py-2 text-sm"
                      />
                      <p className="text-xs text-muted-foreground mt-2 bg-neutral-50 p-3 border border-border">
                        Preço = Base + (comprimento(m) × preço/metro linear) + Material extra.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Fabric Options */}
          {activeTab === 'fabric' && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 p-4 rounded text-sm">
                <p className="font-medium text-green-800 mb-2">Como configurar tecidos:</p>
                <ul className="text-green-700 text-xs space-y-1 list-disc ml-4">
                  <li><strong>Nome:</strong> Nome do tecido (ex: "Linho Natural", "Veludo Azul")</li>
                  <li><strong>Preço por m²:</strong> Quanto custa cada metro quadrado do tecido (ex: 150.00, 280.00)</li>
                </ul>
                <p className="text-green-600 text-xs mt-2 italic">
                  💡 O preço final = Preço Base + (Preço do tecido × M² do tamanho escolhido) + Material extra
                </p>
              </div>
              
              {(productData.fabric_options || []).map((fabric, index) => (
                <div key={fabric.id} className="border border-border p-4 space-y-4">
                  <div className="flex justify-between items-start">
                    <p className="text-xs font-accent uppercase tracking-widest text-muted-foreground">
                      Tecido {index + 1}
                    </p>
                    <button
                      type="button"
                      onClick={() => removeFabricOption(index)}
                      className="text-destructive hover:text-destructive/70"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Nome do Tecido</label>
                      <input
                        type="text"
                        placeholder="Ex: Linho Natural, Veludo Azul"
                        value={fabric.name}
                        onChange={(e) => updateFabricOption(index, 'name', e.target.value)}
                        className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Preço por M² (R$)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Ex: 150.00, 280.00, 450.00"
                        value={fabric.price_per_m2}
                        onChange={(e) => updateFabricOption(index, 'price_per_m2', e.target.value)}
                        className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-2 text-sm"
                      />
                    </div>
                  </div>
                  
                  <OptionImageUpload
                    currentImage={fabric.image}
                    onImageChange={(url) => updateFabricOption(index, 'image', url)}
                    label="Imagem do tecido"
                  />
                </div>
              ))}

              <button
                type="button"
                onClick={addFabricOption}
                className="flex items-center gap-2 text-sm text-primary hover:text-primary/70 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Adicionar Tecido
              </button>
            </div>
          )}

          {/* Size Options */}
          {activeTab === 'size' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 p-4 rounded text-sm">
                <p className="font-medium text-blue-800 mb-2">Como configurar tamanhos:</p>
                <ul className="text-blue-700 text-xs space-y-1 list-disc ml-4">
                  <li><strong>Nome:</strong> Ex: "2 Lugares", "3 Lugares", "Grande"</li>
                  <li><strong>M² de tecido:</strong> Quantidade de tecido necessária (ex: 4.5, 6.0, 8.5)</li>
                  <li><strong>Descrição:</strong> Dimensões do produto (ex: "180cm x 90cm")</li>
                </ul>
                <p className="text-blue-600 text-xs mt-2 italic">
                  ⚠️ O campo "M² de tecido" deve ser um número pequeno (geralmente entre 2 e 15). NÃO coloque o preço aqui!
                </p>
              </div>
              
              {(productData.size_options || []).map((size, index) => (
                <div key={size.id} className="border border-border p-4 space-y-4">
                  <div className="flex justify-between items-start">
                    <p className="text-xs font-accent uppercase tracking-widest text-muted-foreground">
                      Tamanho {index + 1}
                    </p>
                    <button
                      type="button"
                      onClick={() => removeSizeOption(index)}
                      className="text-destructive hover:text-destructive/70"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Nome do Tamanho</label>
                      <input
                        type="text"
                        placeholder="Ex: 2 Lugares, Médio, Grande"
                        value={size.name}
                        onChange={(e) => updateSizeOption(index, 'name', e.target.value)}
                        className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">M² de Tecido Necessário</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="50"
                        placeholder="Ex: 4.5, 6.0, 8.5"
                        value={size.m2_needed}
                        onChange={(e) => updateSizeOption(index, 'm2_needed', e.target.value)}
                        className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-2 text-sm"
                      />
                      {size.m2_needed > 50 && (
                        <p className="text-xs text-red-500 mt-1">⚠️ Valor muito alto! Deveria ser entre 2 e 15 m²</p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs text-muted-foreground mb-1 block">Descrição / Dimensões</label>
                      <input
                        type="text"
                        placeholder="Ex: 180cm x 90cm, Largura 2m"
                        value={size.description}
                        onChange={(e) => updateSizeOption(index, 'description', e.target.value)}
                        className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-2 text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addSizeOption}
                className="flex items-center gap-2 text-sm text-primary hover:text-primary/70 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Adicionar Tamanho
              </button>
            </div>
          )}

          {/* Material Options */}
          {activeTab === 'material' && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 p-4 rounded text-sm">
                <p className="font-medium text-amber-800 mb-2">Como configurar materiais extras:</p>
                <ul className="text-amber-700 text-xs space-y-1 list-disc ml-4">
                  <li><strong>Nome:</strong> Nome do material (ex: "Pés de Madeira", "Tampo de Vidro")</li>
                  <li><strong>Preço Adicional:</strong> Valor fixo que será somado ao preço (ex: 300.00, 500.00)</li>
                </ul>
                <p className="text-amber-600 text-xs mt-2 italic">
                  💡 Este é um valor fixo que será adicionado ao preço total, independente do tamanho escolhido.
                </p>
              </div>
              
              {(productData.material_options || []).map((material, index) => (
                <div key={material.id} className="border border-border p-4 space-y-4">
                  <div className="flex justify-between items-start">
                    <p className="text-xs font-accent uppercase tracking-widest text-muted-foreground">
                      Material {index + 1}
                    </p>
                    <button
                      type="button"
                      onClick={() => removeMaterialOption(index)}
                      className="text-destructive hover:text-destructive/70"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Nome do Material</label>
                      <input
                        type="text"
                        placeholder="Ex: Pés de Madeira, Tampo de Vidro"
                        value={material.name}
                        onChange={(e) => updateMaterialOption(index, 'name', e.target.value)}
                        className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Preço Adicional (R$)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Ex: 300.00, 500.00"
                        value={material.price_extra}
                        onChange={(e) => updateMaterialOption(index, 'price_extra', e.target.value)}
                        className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-2 text-sm"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs text-muted-foreground mb-1 block">Descrição</label>
                      <input
                        type="text"
                        placeholder="Ex: Madeira de carvalho tratada"
                        value={material.description}
                        onChange={(e) => updateMaterialOption(index, 'description', e.target.value)}
                        className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-2 text-sm"
                      />
                    </div>
                  </div>
                  
                  <OptionImageUpload
                    currentImage={material.image}
                    onImageChange={(url) => updateMaterialOption(index, 'image', url)}
                    label="Imagem do material (opcional)"
                  />
                </div>
              ))}

              <button
                type="button"
                onClick={addMaterialOption}
                className="flex items-center gap-2 text-sm text-primary hover:text-primary/70 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Adicionar Material
              </button>
            </div>
          )}

          <div className="mt-6 p-4 bg-secondary">
            <p className="text-xs text-muted-foreground">
              <strong>Como funciona:</strong> O preço final será calculado automaticamente:
              <br />
              Preço Base + (Preço do Tecido por m² × m² Necessários do Tamanho) + Preço do Material Extra
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductCustomization;
